import emptySplitApi from "../utils/rtk";
import {
  AgentList,
  CommonBody,
  CompanyList,
  UsersList,
  VisaApplicant,
} from "../types/General";
import { END_POINTS } from "../constants/urls";
import { ApplicationList } from "../types/User";

type CommonResponseType = {
  status: number;
  message: string;
};
type GetUserResponse = {
  length: number;
  start: number;
  response_object: UsersList[] | VisaApplicant[];
  total_records: number;
  from_date?: string;
  to_date?: string;
};
type GetAgentResponse = {
  length: number;
  start: number;
  response_object: AgentList[];
  total_records: number;
  from_date?: string;
  to_date?: string;
};
type GetCompaniesResponse = {
  length: number;
  start: number;
  response_object: CompanyList[];
  total_records: number;
  from_date?: string;
  to_date?: string;
};
type GetApplicationsResponse = {
  length: number;
  start: number;
  response_object: ApplicationList[];
  total_records: number;
  from_date?: string;
  to_date?: string;
};

export const onBoardingApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    individualUsersList: builder.mutation<
      CommonResponseType & { data: GetUserResponse },
      CommonBody
    >({
      query: (body) => ({
        url: END_POINTS.individualListings,
        method: "POST",
        body,
      }),
    }),
    companyList: builder.mutation<
      CommonResponseType & { data: GetCompaniesResponse },
      CommonBody
    >({
      query: (body) => ({
        url: END_POINTS.companyListings,
        method: "POST",
        body,
      }),
    }),
    agentList: builder.mutation<
      CommonResponseType & { data: GetAgentResponse },
      CommonBody
    >({
      query: (body) => ({
        url: END_POINTS.agentListings,
        method: "POST",
        body,
      }),
    }),
    deleteUser: builder.query<
      CommonResponseType & { data: UsersList },
      { id: number | null }
    >({
      query: ({ id }) => ({
        url: `${END_POINTS.deleteUser}${id}/`,
        method: "DELETE",
      }),
    }),
    changeUserStatus: builder.mutation<
      CommonResponseType & { data: UsersList },
      { body: CommonBody; id: number }
    >({
      query: ({ body, id }) => ({
        url: `${END_POINTS.changeUserStatus}${id}/`,
        method: "POST",
        body,
      }),
    }),
    userApproval: builder.mutation<
      CommonResponseType & { data: UsersList },
      { body: CommonBody; id: number | null }
    >({
      query: ({ body, id }) => ({
        url: `${END_POINTS.userApproval}${id}/`,
        method: "POST",
        body,
      }),
    }),
    userDetails: builder.mutation<
      CommonResponseType & { data: UsersList | AgentList | CompanyList | null },
      { body: CommonBody; id: number }
    >({
      query: ({ body, id }) => ({
        url: `${END_POINTS.getUserById}${id}/`,
        method: "POST",
        body,
      }),
    }),
    updateUser: builder.mutation<
      CommonResponseType & { data: UsersList },
      { body: CommonBody; id: number }
    >({
      query: ({ body, id }) => ({
        url: `${END_POINTS.updateUser}${id}/`,
        method: "POST",
        body,
      }),
    }),
    addSalariedAgent: builder.mutation<
      CommonResponseType & { data: UsersList },
      CommonBody
    >({
      query: (body) => ({
        url: END_POINTS.addSalariedAgent,
        method: "POST",
        body,
      }),
    }),
    exportCsv: builder.mutation<
      CommonResponseType & { file_urls: string },
      CommonBody
    >({
      query: (body) => ({
        url: END_POINTS.exportCsv,
        method: "POST",
        body,
      }),
    }),
    individualApplicationsUserId: builder.mutation<
      CommonResponseType & { data: GetApplicationsResponse },
      { body: CommonBody; id: number }
    >({
      query: ({ body, id }) => ({
        url: `${END_POINTS.individualApplicationsById}${id}/`,
        method: "POST",
        body,
      }),
    }),
    partnerApplicationsByUserId: builder.mutation<
      CommonResponseType & { data: GetApplicationsResponse },
      { body: CommonBody; id: number }
    >({
      query: ({ body, id }) => ({
        url: `${END_POINTS.partnerApplicationsById}${id}/`,
        method: "POST",
        body,
      }),
    }),
    companyCount: builder.query<
      CommonResponseType & {
        data: {
          new_companies: number;
          approved_companies: number;
          rejected_companies: number;
        };
      },
      void
    >({
      query: () => ({
        url: `${END_POINTS.companyCount}`,
        method: "GET",
      }),
    }),
    agentCount: builder.query<
      CommonResponseType & {
        data: {
          salaried_agents: number;
          freelancer_agents: {
            total_agents: number;
            approved_freelancer: number;
            new_freelancer: number;
            Rejected_freelancer: number;
          };
        };
      },
      void
    >({
      query: () => ({
        url: `${END_POINTS.agentCount}`,
        method: "GET",
      }),
    }),

    agentCommissionById: builder.mutation<
      CommonResponseType & { data: GetApplicationsResponse },
      { body: CommonBody; id: number }
    >({
      query: ({ body, id }) => ({
        url: `${END_POINTS.agentCommissionById}${id}/`,
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useIndividualUsersListMutation,
  useLazyDeleteUserQuery,
  useChangeUserStatusMutation,
  useUserApprovalMutation,
  useUserDetailsMutation,
  useUpdateUserMutation,
  useCompanyListMutation,
  useAgentListMutation,
  useAddSalariedAgentMutation,
  useExportCsvMutation,
  useIndividualApplicationsUserIdMutation,
  usePartnerApplicationsByUserIdMutation,
  useLazyCompanyCountQuery,
  useLazyAgentCountQuery,
  useAgentCommissionByIdMutation,
} = onBoardingApi;
